/**
 * What a field user's Forms list does with the `route` the API hands it.
 *
 * ONE copy, shared by the two doors into the same library:
 *   - `useFieldForms`      → GET /field/forms      (drivers and rental techs)
 *   - `useAvailableForms`  → GET /forms/available  (everyone, casing included)
 *
 * They are two endpoints because one resolves a `trucking_drivers` row and one
 * must not, but the rows they return come from the same `form_templates` table
 * and carry the same `route` column. A second copy of this logic is how the two
 * lists would start disagreeing about which forms open.
 */

/**
 * Routes the form_templates seed data ships (migrations 0156, 0181, 0184) that
 * no router entry serves. Every field tree ends in a catch-all `<Route path="*">`,
 * so tapping one of these does not 404 — it bounces the user to that tree's home
 * screen with no explanation, and the tap reads as broken rather than unbuilt.
 * The forms do not exist yet and the frontend cannot supply them; it can only
 * stop pretending they open.
 *
 * This is a denylist of dead routes rather than an allowlist of working ones on
 * purpose: a route missing from this set stays dead, which is today's behaviour,
 * whereas a route missing from an allowlist would hide a form that works. Drop
 * an entry from this set in the same commit that adds its <Route>.
 */
export const UNIMPLEMENTED_FORM_ROUTES = new Set([
  '/forms/pre-trip',
  '/forms/post-trip',
  '/forms/hazard',
  '/forms/asset-condition',
  '/forms/pipe-tally',
  '/forms/tong-truck-inspection',
]);

/** The stub `form_templates.route` every AI-replicated and hand-built form carries. */
const DYNAMIC_ROUTE_STUB = '/forms/dynamic/';

/**
 * `/forms/dynamic/` → `/forms/dynamic/<template id>`.
 *
 * The column holds the stub WITHOUT the id — `createTemplate` and
 * `cloneTemplate` both write the literal `'/forms/dynamic/'`, and the seed data
 * for every replicated form does the same. The router registers
 * `/forms/dynamic/:templateId`, which the stub does not match, so navigating to
 * the raw column value lands on the tree's catch-all: the crew taps a form the
 * tenant published and gets put back on the home screen.
 *
 * Every other route (`/forms/pre-job-meeting` and the dead ones above) is a real
 * path and is passed through untouched.
 */
export function resolveFormRoute(route: string | undefined | null, templateId: string): string {
  const raw = route ?? '';
  if (raw === DYNAMIC_ROUTE_STUB || raw === '/forms/dynamic') {
    return `${DYNAMIC_ROUTE_STUB}${templateId}`;
  }
  return raw;
}

/** Does the router serve this route, or would tapping it bounce to the catch-all? */
export function isDeadFormRoute(route: string | undefined | null): boolean {
  return UNIMPLEMENTED_FORM_ROUTES.has(route ?? '');
}
